import React, { useEffect, useRef } from 'react';
import {
  Check,
  ChevronDown,
  Headphones,
  Laptop,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Smartphone,
  Speaker,
  Volume2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSpotify } from '../../contexts/SpotifyContext';
import { ROUTES } from '../../constants/routes';
import SpotifyIcon from './SpotifyIcon';

function DeviceIcon({ type, className }) {
  const t = String(type || '').toLowerCase();
  if (t.includes('smartphone') || t.includes('phone')) {
    return <Smartphone className={className} />;
  }
  if (t.includes('speaker') || t.includes('cast')) {
    return <Speaker className={className} />;
  }
  return <Laptop className={className} />;
}

function formatMs(ms) {
  if (!ms || ms < 0) return '0:00';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const SpotifyPlayer = () => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const {
    connected,
    currentTrack,
    isPlaying,
    position,
    duration,
    volume,
    isPremium,
    accountError,
    deviceId,
    devices,
    expanded,
    setExpanded,
    togglePlay,
    nextTrack,
    previousTrack,
    seek,
    setVolume,
    transferToDevice,
    startFocusPlaylist,
    focusPlaylistUri,
    fetchDevices,
  } = useSpotify();

  useEffect(() => {
    if (!expanded) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [expanded, setExpanded]);

  if (!connected) return null;

  const progress = duration > 0 ? Math.min(100, (position / duration) * 100) : 0;
  const title = currentTrack?.name || 'No track playing';
  const subtitle =
    currentTrack?.artist ||
    (accountError
      ? 'Open Spotify to play'
      : isPremium
        ? deviceId
          ? 'Ready'
          : 'Play a song in Spotify'
        : 'Premium needed');

  return (
    <div className="relative" ref={rootRef} data-testid="spotify-mini-player">
      {/* Compact pill */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="group flex items-center gap-2 h-9 max-w-[13rem] sm:max-w-[16rem] rounded-full bg-secondary-900/5 dark:bg-white/[0.06] hover:bg-secondary-900/10 dark:hover:bg-white/[0.1] backdrop-blur-sm pl-1 pr-2.5 transition-all duration-200"
        aria-label="Spotify now playing"
        aria-expanded={expanded}
      >
        {/* Album art / icon */}
        <div className="relative flex-shrink-0 h-7 w-7 rounded-full overflow-hidden bg-secondary-200 dark:bg-secondary-700 ring-2 ring-[#1DB954]/40">
          {currentTrack?.albumArt ? (
            <img
              src={currentTrack.albumArt}
              alt=""
              className={`h-full w-full object-cover ${isPlaying ? 'animate-[spin_8s_linear_infinite]' : ''}`}
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-[#1DB954]/20">
              <SpotifyIcon className="h-3.5 w-3.5 text-[#1DB954]" />
            </div>
          )}
          {isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-2 w-2 rounded-full bg-white/80 shadow-sm" />
            </div>
          )}
        </div>

        {/* Track info */}
        <div className="min-w-0 flex-1 text-left hidden sm:block">
          <p className="text-[11px] font-medium text-secondary-800 dark:text-secondary-100 truncate leading-tight">
            {title}
          </p>
          <p className="text-[10px] text-secondary-500 dark:text-secondary-400 truncate leading-tight">
            {subtitle}
          </p>
        </div>

        {/* Animated bars indicator */}
        {isPlaying && (
          <div className="flex items-end gap-[2px] h-3 flex-shrink-0">
            <span className="w-[2px] bg-[#1DB954] rounded-full animate-[equalize_0.8s_ease-in-out_infinite]" style={{ height: '60%' }} />
            <span className="w-[2px] bg-[#1DB954] rounded-full animate-[equalize_0.8s_ease-in-out_0.2s_infinite]" style={{ height: '100%' }} />
            <span className="w-[2px] bg-[#1DB954] rounded-full animate-[equalize_0.8s_ease-in-out_0.4s_infinite]" style={{ height: '40%' }} />
          </div>
        )}

        {/* Chevron */}
        <ChevronDown
          className={`h-3 w-3 text-secondary-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Expanded dropdown */}
      {expanded && (
        <div className="absolute right-0 top-full mt-2.5 w-80 rounded-2xl border border-secondary-200/80 dark:border-secondary-700/80 bg-white dark:bg-secondary-800 shadow-2xl shadow-black/10 dark:shadow-black/30 z-50 overflow-hidden animate-fade-in">
          {/* Progress bar at top */}
          <div className="h-[3px] w-full bg-secondary-100 dark:bg-secondary-700">
            <div
              className="h-full rounded-r-full transition-all duration-500 ease-linear"
              style={{ width: `${progress}%`, backgroundColor: '#1DB954' }}
            />
          </div>

          <div className="p-4 space-y-4">
            {/* Track info */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-14 w-14 rounded-xl overflow-hidden bg-secondary-100 dark:bg-secondary-700 flex-shrink-0 shadow-md">
                {currentTrack?.albumArt ? (
                  <img
                    src={currentTrack.albumArt}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-[#1DB954]/20 to-[#1DB954]/5">
                    <SpotifyIcon className="h-7 w-7 text-[#1DB954]" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-secondary-900 dark:text-white truncate">
                  {title}
                </p>
                <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                  {subtitle}
                </p>
                {currentTrack?.album && (
                  <p className="text-[10px] text-secondary-400 dark:text-secondary-500 truncate mt-0.5">
                    {currentTrack.album}
                  </p>
                )}
              </div>
            </div>

            {accountError && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 rounded-lg">{accountError}</p>
            )}

            {!isPremium && (
              <p className="text-[11px] text-secondary-500 bg-secondary-50 dark:bg-secondary-700/50 px-2.5 py-1.5 rounded-lg" data-testid="spotify-free-hint">
                Upgrade to Spotify Premium for in-app playback controls.
              </p>
            )}
            {isPremium && !currentTrack && !accountError && (
              <p className="text-[11px] text-secondary-500 bg-secondary-50 dark:bg-secondary-700/50 px-2.5 py-1.5 rounded-lg">
                Open Spotify on your phone or computer and start a song — Tick Sync will remote-control that device.
              </p>
            )}

            {isPremium && (
              <>
                {/* Seek bar */}
                <div className="space-y-1">
                  <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    value={Math.min(position, duration || 0)}
                    onChange={(e) => seek(Number(e.target.value))}
                    className="w-full accent-[#1DB954] h-1 cursor-pointer"
                    aria-label="Seek"
                    data-testid="spotify-seek"
                  />
                  <div className="flex justify-between text-[10px] text-secondary-400 font-mono tabular-nums">
                    <span>{formatMs(position)}</span>
                    <span>{formatMs(duration)}</span>
                  </div>
                </div>

                {/* Transport controls */}
                <div className="flex items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      previousTrack();
                    }}
                    className="p-2 rounded-full text-secondary-500 hover:text-secondary-800 dark:hover:text-white hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
                    aria-label="Previous track"
                    data-testid="spotify-prev"
                  >
                    <SkipBack className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      togglePlay();
                    }}
                    className="p-3 rounded-full text-white shadow-lg hover:scale-105 transition-transform"
                    style={{ backgroundColor: '#1DB954' }}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                    data-testid="spotify-play-pause"
                  >
                    {isPlaying ? (
                      <Pause className="h-5 w-5" />
                    ) : (
                      <Play className="h-5 w-5 ml-0.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      nextTrack();
                    }}
                    className="p-2 rounded-full text-secondary-500 hover:text-secondary-800 dark:hover:text-white hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
                    aria-label="Next track"
                    data-testid="spotify-next"
                  >
                    <SkipForward className="h-4 w-4" />
                  </button>
                </div>

                {/* Volume */}
                <div className="flex items-center gap-2.5 px-1">
                  <Volume2 className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={volume}
                    onChange={(e) => setVolume(Number(e.target.value))}
                    className="flex-1 accent-[#1DB954] h-1 cursor-pointer"
                    aria-label="Volume"
                    data-testid="spotify-volume"
                  />
                </div>

                {focusPlaylistUri && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      startFocusPlaylist();
                    }}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#1DB954]/40 bg-[#1DB954]/10 hover:bg-[#1DB954]/20 text-[#1DB954] text-xs font-medium py-2 transition-colors"
                    data-testid="spotify-focus-play"
                  >
                    <Headphones className="h-3.5 w-3.5" />
                    Play focus playlist
                  </button>
                )}

                <div className="space-y-1.5" data-testid="spotify-devices">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-medium text-secondary-500 dark:text-secondary-400">
                      Devices
                    </p>
                    <button
                      type="button"
                      onClick={() => fetchDevices()}
                      className="text-[10px] text-[#1DB954] hover:underline"
                    >
                      Refresh
                    </button>
                  </div>
                  {devices.length === 0 ? (
                    <p className="text-[11px] text-secondary-400">
                      No devices found. Open Spotify on a phone or computer.
                    </p>
                  ) : (
                    <ul className="max-h-28 overflow-y-auto space-y-0.5">
                      {devices.map((device) => (
                        <li key={device.id}>
                          <button
                            type="button"
                            onClick={() => transferToDevice(device.id, { play: isPlaying })}
                            className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${
                              device.is_active
                                ? 'bg-[#1DB954]/15 text-[#1DB954]'
                                : 'hover:bg-secondary-100 dark:hover:bg-secondary-700 text-secondary-700 dark:text-secondary-200'
                            }`}
                            data-testid={`spotify-device-${device.id}`}
                          >
                            <DeviceIcon type={device.type} className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate flex-1">{device.name}</span>
                            {device.is_active && <Check className="h-3.5 w-3.5 flex-shrink-0" />}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}

            {/* Footer link */}
            <div className="pt-2 border-t border-secondary-100 dark:border-secondary-700/60">
              <button
                type="button"
                onClick={() => {
                  setExpanded(false);
                  navigate(ROUTES.SPOTIFY);
                }}
                className="flex items-center gap-1.5 text-[11px] text-[#1DB954] hover:text-[#1ed760] font-medium transition-colors"
              >
                <SpotifyIcon className="h-3 w-3" />
                Spotify settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpotifyPlayer;
